/**
 * A2UI交互组件库 (v0.9 深度适配版)
 * 支持原子交互与事务性表单模式
 * 引入 FormContext 进行状态管理
 */

import React, { useState, useCallback, useContext, createContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import './A2UIComponents.css';

// 创建表单上下文
const FormContext = createContext({
  formData: {},
  updateField: () => {},
  submitForm: () => {},
  registerField: () => {} // 🆕 新增：注册字段元数据
});

/**
 * 快速选择组件 (Choice) - 对应 A2UI ChoicePicker
 * 支持单选/多选，支持直接触发动作或仅更新状态
 * 方案B：选择时传递 label 以生成友好的 displayText
 */
export const ChoiceComponent = ({ 
  options, 
  onSelect, 
  // 新增属性
  field,        // 绑定的数据字段名
  mode = 'single', // 'single' | 'multiple'
  action,       // 如果存在，选择后立即触发Action (仅单选模式)
  label         // 组标签
}) => {
  const { t } = useTranslation();
  const { formData, updateField, submitForm, registerField } = useContext(FormContext);
  
  // 🆕 注册字段元数据
  useEffect(() => {
    if (field && label && registerField) {
      registerField(field, label);
    }
  }, [field, label, registerField]);

  // 本地状态（用于非表单环境或立即反馈）
  const [localSelection, setLocalSelection] = useState(mode === 'multiple' ? [] : null);
  // 🆕 "其他想法" 的补充输入
  const [otherText, setOtherText] = useState('');
  
  // 决定当前值：优先使用Context中的值，否则使用本地状态
  const currentValue = field ? (formData[field] || (mode === 'multiple' ? [] : null)) : localSelection;

  // 🆕 是否选中了 other
  const isOtherSelected = mode === 'multiple'
    ? (currentValue || []).includes('other')
    : currentValue === 'other';

  const handleSelect = (optionValue, optionLabel) => {
    let newValue;
    
    if (mode === 'multiple') {
      const currentSet = new Set(currentValue || []);
      if (currentSet.has(optionValue)) {
        currentSet.delete(optionValue);
      } else {
        currentSet.add(optionValue);
      }
      newValue = Array.from(currentSet);
    } else {
      newValue = optionValue;
    }

    // 1. 更新状态（同时传递 label）
    if (field) {
      updateField(field, newValue, optionLabel);
    } else {
      setLocalSelection(newValue);
    }

    // 🆕 选择"其他想法"时，不要在单选模式下立即提交；等待用户补充输入
    if (optionValue === 'other') {
      return;
    }

    // 2. 单选模式：立即提交（原子交互模式）
    if (mode === 'single') {
      // 构造上下文数据
      const contextData = field ? { ...formData, [field]: newValue } : { value: newValue };
      const actionName = action || 'choice_submit'; // 🆕 默认 action
      
      if (submitForm) {
        submitForm(actionName, contextData, { displayText: optionLabel, autoSend: true });
      } else if (onSelect) {
        onSelect({ name: actionName, context: contextData, displayText: optionLabel });
      }
    }
  };

  // 🆕 单选模式下，"其他想法"输入确认提交
  const handleOtherSubmitSingle = () => {
    const text = otherText.trim();
    if (!text) return;

    // 用用户输入替换 other
    const contextData = field ? { ...formData, [field]: text } : { value: text };
    const actionName = action || 'choice_submit'; // 🆕 默认 action

    if (submitForm) {
      submitForm(actionName, contextData, { displayText: text, autoSend: true });
    } else if (onSelect) {
      onSelect({ name: actionName, context: contextData, displayText: text });
    }
  };

  // 🆕 多选提交处理
  const handleSubmitMultiple = () => {
    // 构造上下文数据
    const contextData = { ...formData };
    if (field) {
       // 确保包含当前字段的最新值（把 other 替换为用户输入）
       let finalValue = currentValue;
       if (Array.isArray(currentValue) && currentValue.includes('other')) {
         const others = currentValue.filter(v => v !== 'other');
         const text = otherText.trim();
         if (text) others.push(text);
         else others.push(t('a2ui_other_fallback')); // 兜底
         finalValue = others;
       }
       contextData[field] = finalValue;
    }
    
    // 生成 displayText: 将选中的 value 转换为 label
    let displayParts = [];
    const valueForDisplay = field ? contextData[field] : currentValue;
    if (valueForDisplay && valueForDisplay.length > 0) {
       displayParts = valueForDisplay.map(val => {
          // 尝试找到对应的 label
          const opt = options.find(o => (typeof o === 'object' ? o.value : o) === val);
          return opt ? (typeof opt === 'object' ? opt.label : opt) : val;
       });
    }
    const displayText = displayParts.join('；');

    if (submitForm) {
        submitForm(action || 'choice_submit', contextData, { displayText, autoSend: true });
    }
  };
  
  if (!options || options.length === 0) return null;
  
  return (
    <div className="a2ui-choice-container">
      {label && <div className="a2ui-component-label">{label}</div>}
      {options.map((option, index) => {
        // 解析选项对象或字符串
        const optLabel = typeof option === 'object' ? option.label : option;
        const optValue = typeof option === 'object' ? option.value : option;
        
        const isSelected = mode === 'multiple' 
          ? (currentValue || []).includes(optValue)
          : currentValue === optValue;

        return (
          <button
            key={index}
            className={`a2ui-choice-btn ${isSelected ? 'selected' : ''}`}
            onClick={() => handleSelect(optValue, optLabel)}
            type="button"
          >
            <ReactMarkdown
              remarkPlugins={[remarkMath]}
              rehypePlugins={[[rehypeKatex, { throwOnError: false, strict: 'ignore' }]]}
              components={{
                // 防止 ReactMarkdown 包裹 <p> 导致布局问题
                p: ({ children }) => <span>{children}</span>
              }}
            >
              {optLabel}
            </ReactMarkdown>
          </button>
        );
      })}

      {/* 🆕 选择“其他想法”后显示补充输入框 */}
      {isOtherSelected && (
        <div className="a2ui-choice-other-wrapper">
          <textarea
            className="a2ui-choice-other-input"
            placeholder={t('a2ui_other_placeholder')}
            value={otherText}
            onChange={(e) => {
                setOtherText(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && mode === 'single') {
                e.preventDefault();
                handleOtherSubmitSingle();
              }
            }}
            rows={1}
            style={{ resize: 'none', overflowY: 'hidden' }}
            autoFocus
          />
          {mode === 'single' && (
            <button
              type="button"
              className="a2ui-choice-other-confirm"
              onClick={handleOtherSubmitSingle}
              disabled={!otherText.trim()}
            >
              {t('a2ui_confirm')}
            </button>
          )}
        </div>
      )}
      
      {/* 🆕 多选模式兜底提交按钮 */}
      {mode === 'multiple' && (currentValue || []).length > 0 && (
        <button 
            className="a2ui-choice-submit-btn"
            onClick={handleSubmitMultiple}
            type="button"
        >
            {t('a2ui_confirm_selection', { count: (currentValue || []).length })}
        </button>
      )}
    </div>
  );
};

/**
 * 进度指示组件 (Progress)
 * 纯展示组件
 */
export const ProgressComponent = ({ current, total, label }) => {
  const percentage = total > 0 ? (current / total) * 100 : 0;
  
  return (
    <div className="a2ui-progress-container">
      <div className="a2ui-progress-header">
        <span className="a2ui-progress-label">{label}</span>
        <span className="a2ui-progress-count">{current}/{total}</span>
      </div>
      <div className="a2ui-progress-bar">
        <div 
          className="a2ui-progress-fill" 
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

/**
 * 用户输入组件 (Input) - 对应 A2UI TextField
 * 方案B：支持回车提交，自动填入聊天输入框
 */
export const InputComponent = ({ 
  fieldName, // 旧版兼容
  onSubmit,  // 旧版兼容
  // 新增属性
  field,     // 绑定的数据字段名
  label,     // 标签
  placeholder,
  action,    // 🆕 回车触发的动作名
  value: initialValue, // 🆕 初始值
  hideLabel  // 🆕 是否在聚合文本中隐藏Label
}) => {
  const { t } = useTranslation();
  const { formData, updateField, submitForm, registerField } = useContext(FormContext);
  
  // 兼容旧版 fieldName
  const targetField = field || fieldName;
  const displayLabel = label || fieldName;
  const isControlled = !!field; // 是否受控
  
  // 🆕 注册字段元数据
  useEffect(() => {
    // 如果设置了 hideLabel，则不注册 Label，从而在 submitForm 聚合时只显示 Value
    if (targetField && displayLabel && registerField && !hideLabel) {
      registerField(targetField, displayLabel);
    }
  }, [targetField, displayLabel, registerField, hideLabel]);

  // 🆕 初始化默认值
  useEffect(() => {
    if (isControlled && initialValue && formData[targetField] === undefined) {
      updateField(targetField, initialValue);
    }
  }, [isControlled, targetField, initialValue, formData, updateField]);

  // 本地状态（用于非受控模式）
  const [localValue, setLocalValue] = useState(initialValue || '');

  // 决定当前显示值
  const contextValue = formData[targetField];
  const value = isControlled ? (contextValue !== undefined ? contextValue : (initialValue || '')) : localValue;

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (isControlled) {
      updateField(targetField, newValue);
    } else {
      setLocalValue(newValue);
    }
  };
  
  // 🆕 回车提交（方案B+：聚合模式）
  // 支持 Shift+Enter 换行，Enter 提交
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (!e.shiftKey) {
        e.preventDefault(); // 阻止默认换行
        
        const inputValue = isControlled ? value : localValue;
        if (!inputValue.trim()) return;
        
        if (submitForm) {
          // 表单模式：使用 submitForm
          const contextData = { ...formData, [targetField]: inputValue };
          // 🔑 直接使用用户输入作为 displayText，不包含标签
          submitForm(action || 'input_submit', contextData, { 
            displayText: inputValue.trim(), 
            autoSend: true 
          });
        } else if (onSubmit) {
          // 兼容旧版
          onSubmit(targetField, inputValue);
        }
      }
      // 如果按了 Shift，允许默认行为（换行）
    }
  };

  // 🆕 自适应高度处理
  const handleInput = (e) => {
    const target = e.target;
    // 重置高度以计算正确的 scrollHeight (处理删除文字变短的情况)
    target.style.height = 'auto';
    target.style.height = `${target.scrollHeight}px`;
  };
  
  // 监听值变化以调整高度 (用于初始值或外部更新)
  useEffect(() => {
    const textarea = document.getElementById(`a2ui-input-${targetField}`);
    if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [value, localValue, targetField]);
  
  return (
    <div className="a2ui-input-container">
      <label className="a2ui-input-label">{displayLabel}</label>
      <div className="a2ui-input-wrapper">
        <textarea
          id={`a2ui-input-${targetField}`}
          className="a2ui-input-field"
          value={value}
          onChange={(e) => { handleChange(e); handleInput(e); }}
          placeholder={placeholder || t('a2ui_input_placeholder', { label: displayLabel })}
          onKeyDown={handleKeyDown}
          rows={1}
          style={{ resize: 'none', overflowY: 'hidden' }}
        />
        {/* 在原子模式下显示确认按钮 */}
        {!isControlled && (
          <button 
            className="a2ui-input-submit"
            onClick={() => onSubmit && onSubmit(targetField, localValue)}
            disabled={!localValue.trim()}
            type="button"
          >
            {t('a2ui_confirm')}
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * 提示组件 (Tip)
 */
export const TipComponent = ({ type = 'info', children }) => {
  const iconMap = {
    info: 'ℹ️',
    warning: '⚠️',
    success: '✅',
    error: '❌'
  };
  
  return (
    <div className={`a2ui-tip a2ui-tip-${type}`}>
      <span className="a2ui-tip-icon">{iconMap[type] || iconMap.info}</span>
      <div className="a2ui-tip-content">
        {typeof children === 'string' ? (
          <ReactMarkdown
            remarkPlugins={[remarkMath]}
            rehypePlugins={[[rehypeKatex, { throwOnError: false, strict: 'ignore' }]]}
          >
            {children}
          </ReactMarkdown>
        ) : (
          children
        )}
      </div>
    </div>
  );
};

/**
 * 按钮组件 (Button)
 * 事务提交触发器
 * 点击按钮时提交整个表单数据，displayText 由 A2UIContainer 自动聚合
 */
export const ButtonComponent = ({ 
  label, 
  action, // 动作名称
  primary = false, 
  onClick // 旧版兼容
}) => {
  const { submitForm, formData } = useContext(FormContext);
  const [clicked, setClicked] = useState(false);

  const handleClick = () => {
    setClicked(true);
    setTimeout(() => setClicked(false), 200);

    if (submitForm && action) {
      // 事务模式：提交整个表单数据
      // 🆕 不传 displayText，让 A2UIContainer 的 submitForm 自动聚合
      // 如果 formData 为空，回退到按钮 label
      const hasData = Object.values(formData).some(v => 
        v !== undefined && v !== null && v !== '' && 
        (!Array.isArray(v) || v.length > 0)
      );
      submitForm(action, formData, { 
        displayText: hasData ? undefined : label, // 空表单时用按钮文字
        autoSend: true 
      });
    } else if (onClick) {
      // 兼容模式
      onClick(action || label);
    }
  };

  return (
    <button
      className={`a2ui-button ${primary ? 'primary' : ''} ${clicked ? 'clicked' : ''}`}
      onClick={handleClick}
      type="button"
    >
      {label}
    </button>
  );
};

/**
 * A2UI 容器组件
 * 提供 FormContext 上下文
 * 方案B：生成 displayText 供填入聊天输入框
 */
export const A2UIContainer = ({ children, onAction }) => {
  const [formData, setFormData] = useState({});
  // 🆕 存储选项值标签映射（value → label，用于Choice）
  const [optionLabels, setOptionLabels] = useState({});
  // 🆕 存储字段名标签映射（field → label，用于Input）
  const [fieldLabels, setFieldLabels] = useState({});

  const updateField = useCallback((field, value, label = null) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // 如果提供了 label (Choice组件)，保存值映射
    if (label && value) {
      setOptionLabels(prev => ({ ...prev, [value]: label }));
    }
  }, []);

  // 🆕 注册字段元数据（Input组件使用）
  const registerField = useCallback((field, label) => {
    setFieldLabels(prev => ({ ...prev, [field]: label }));
  }, []);

  const submitForm = useCallback((actionName, contextData = null, options = {}) => {
    // 优先使用传入的contextData，否则使用当前formData
    const data = contextData || formData;
    
    // 🆕 生成自然语言描述 (displayText)
    let displayText = options.displayText;
    if (!displayText) {
      const textParts = [];
      Object.entries(data).forEach(([field, value]) => {
        if (value == null || value === '' || value === 'other') return;
        
        // 1. 处理值的显示 (Choice组件的值可能有label)
        const valueLabel = optionLabels[value] || value;
        const valueText = Array.isArray(valueLabel) ? valueLabel.join('；') : String(valueLabel);
        
        // 2. 处理字段的显示 (Input组件有字段Label)
        const fieldLabel = fieldLabels[field];
        
        if (fieldLabel) {
          // 如果有字段标签，显示 "标签: 值"
          textParts.push(`${fieldLabel}: ${valueText}`);
        } else {
          // 否则直接显示值
          textParts.push(valueText);
        }
      });
      displayText = textParts.join('；');
    }
    
    if (onAction) {
      onAction({
        name: actionName,
        context: data,
        displayText: displayText,
        autoSend: options.autoSend || false,
        timestamp: new Date().toISOString()
      });
    }
  }, [formData, optionLabels, fieldLabels, onAction]);

  return (
    <FormContext.Provider value={{ formData, updateField, submitForm, registerField, optionLabels }}>
      <div className="a2ui-surface">
        {children}
      </div>
    </FormContext.Provider>
  );
};

export default {
  ChoiceComponent,
  ProgressComponent,
  InputComponent,
  TipComponent,
  ButtonComponent,
  A2UIContainer
};
